import { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
  useParams,
  useNavigate,
} from "react-router-dom";
import { Suspense } from "react";
import {
  preloadedComponents,
  preloadAllComponents,
} from "./lib/ComponentPreloader";

// Use preloaded components for instant navigation
const {
  Profile,
  Install,
  Login,
  Verify,
  ProfileSetup,
  Messages,
  Notifications,
  NotificationHandler,
  CoPilot,
  Attendees,
  Favorites,
  Section,
  SectionDetail,
} = preloadedComponents;
import { Sidebar } from "./components/Sidebar";
import { Header } from "./components/Header";
import { NoEventPage, LoadingPage } from "./components/EmptyPage";
import { AttendeeProvider, useAttendee } from "./context/AttendeeContext";
import { EventProvider } from "./context/EventContext";
import { HeaderProvider, useHeader } from "./context/HeaderContext";
import { FavoritesProvider } from "./context/FavoritesContext";
import { ChatProvider } from "./context/ChatContext";
import { SectionsProvider } from "./context/SectionsContext";
import { BannersProvider } from "./context/BannersContext";
import { initializeNotificationMessageHandler } from "./lib/notificationMessageHandler";
import { handleApiKeyAuth, isApiKeyPreviewMode } from "./lib/apiKeyAuth";
import UpdatePrompt from "./components/UpdatePrompt";
import "./App.css";

// Component to redirect from /e/:eventSlug to /e/:eventSlug/copilot
function EventRedirect() {
  const { eventSlug } = useParams();
  return <Navigate to={`/e/${eventSlug}/copilot`} replace />;
}

// Component to show empty page for routes without event slug
function EmptyPage() {
  return <NoEventPage />;
}

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const {
    attendee,
    isProfileComplete,
    loading: attendeeLoading,
  } = useAttendee();
  const { actions, subtitle, searchProps } = useHeader();

  // Preload components on app start for instant navigation
  useEffect(() => {
    preloadAllComponents();
  }, []);

  // Check if we're on the install page
  const isInstallPage = location.pathname.includes("/install");

  // Check if we're on auth pages
  const isAuthPage =
    location.pathname.includes("/login") ||
    location.pathname.includes("/verify") ||
    location.pathname.includes("/profile-setup");

  // Check if we're on the empty page (no event slug or just /e/ with no slug)
  const isEmptyPage =
    !location.pathname.startsWith("/e/") || location.pathname === "/e/";

  // Check if user is authenticated
  const isAuthenticated = () => {
    return !!attendee;
  };

  // Handle API key authentication on app startup
  useEffect(() => {
    const initializeAuth = async () => {
      // Check if we're in API key preview mode
      const isPreviewMode = isApiKeyPreviewMode();

      if (isPreviewMode) {
        try {
          await handleApiKeyAuth();
        } catch (error) {
          // Handle error silently
        }
      }
    };

    // Only run once on mount
    initializeAuth();
  }, []); // Empty dependency array ensures this only runs once

  // Redirect to login if not authenticated and not on auth/install pages
  useEffect(() => {
    // Don't redirect while attendee data is still loading
    if (attendeeLoading) return;

    // Don't redirect if we're in API key preview mode
    if (isApiKeyPreviewMode()) return;

    if (!isInstallPage && !isAuthPage && !isAuthenticated()) {
      const pathParts = location.pathname.split("/").filter(Boolean);
      const eventSlug = pathParts[1]; // /e/:eventSlug format
      if (eventSlug) {
        window.location.href = `/e/${eventSlug}/login`;
      }
    }
  }, [location.pathname, isInstallPage, isAuthPage, attendeeLoading]);

  // Redirect to profile setup if authenticated but profile incomplete
  useEffect(() => {
    // Don't redirect while attendee data is still loading
    if (attendeeLoading) return;

    if (
      !isInstallPage &&
      !isAuthPage &&
      isAuthenticated() &&
      !isProfileComplete()
    ) {
      const pathParts = location.pathname.split("/").filter(Boolean);
      const eventSlug = pathParts[1]; // /e/:eventSlug format
      if (eventSlug && !location.pathname.includes("/profile-setup")) {
        window.location.href = `/e/${eventSlug}/profile-setup`;
      }
    }
  }, [
    location.pathname,
    isInstallPage,
    isAuthPage,
    isProfileComplete,
    attendeeLoading,
  ]);

  useEffect(() => {
    // Listen for the appinstalled event
    window.addEventListener("appinstalled", () => {
      // PWA was installed
    });

    return () => {
      // Cleanup handled by Install.jsx page
    };
  }, []);

  // Initialize notification message handler
  useEffect(() => {
    // Extract event slug from current path
    const pathParts = location.pathname.split("/").filter(Boolean);
    const eventSlug = pathParts[1]; // /e/:eventSlug format

    if (eventSlug) {
      initializeNotificationMessageHandler(navigate, eventSlug);
    }
  }, [location.pathname, navigate]);

  // Show loading screen while attendee data is being loaded
  if (attendeeLoading) {
    return (
      <LoadingPage
        title="Loading..."
        subtitle="Please wait while we load your data."
      />
    );
  }

  return (
    <div className="min-h-screen">
      {/* Update Prompt - show on all pages */}
      <UpdatePrompt />

      {/* Sidebar - only show on non-install, non-auth, and non-empty pages */}
      {!isInstallPage && !isAuthPage && !isEmptyPage && (
        <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      )}

      {/* Main App Content */}
      <div
        className={
          !isInstallPage && !isAuthPage && !isEmptyPage ? "lg:pl-72" : ""
        }
      >
        {/* Header - show on all pages except install and empty page, hidden on mobile */}
        {!isInstallPage && !isEmptyPage && (
          <div className="hidden lg:block">
            <Header
              showBackButton={isAuthPage}
              actions={actions}
              subtitle={subtitle}
              searchProps={searchProps}
            />
          </div>
        )}
        <div>
          <Suspense
            fallback={
              <LoadingPage
                title="Loading page..."
                subtitle="Please wait while we load the page."
              />
            }
          >
            <Routes>
              {/* Public routes (no authentication required) */}
              <Route path="/e/:eventSlug/install" element={<Install />} />

              {/* Redirect from /e/:eventSlug to /e/:eventSlug/copilot */}
              <Route path="/e/:eventSlug" element={<EventRedirect />} />

              {/* Authentication routes */}
              <Route path="/e/:eventSlug/login" element={<Login />} />
              <Route path="/e/:eventSlug/verify" element={<Verify />} />
              <Route
                path="/e/:eventSlug/profile-setup"
                element={<ProfileSetup />}
              />

              {/* Protected routes (authentication required) */}
              <Route path="/e/:eventSlug/messages" element={<Messages />} />
              <Route
                path="/e/:eventSlug/notifications"
                element={<Notifications />}
              />
              <Route
                path="/e/:eventSlug/notification"
                element={<NotificationHandler />}
              />
              <Route path="/e/:eventSlug/copilot" element={<CoPilot />} />
              <Route path="/e/:eventSlug/attendees" element={<Attendees />} />
              <Route path="/e/:eventSlug/favorites" element={<Favorites />} />
              <Route path="/e/:eventSlug/sections/:id" element={<Section />} />
              <Route
                path="/e/:eventSlug/sections/:sectionId/:id"
                element={<SectionDetail />}
              />
              <Route path="/e/:eventSlug/profile" element={<Profile />} />
              {/* Catch-all route for paths without event slug  */}
              <Route path="*" element={<EmptyPage />} />
            </Routes>
          </Suspense>
        </div>
      </div>
    </div>
  );
}

function App() {
  return (
    <Router>
      <EventProvider>
        <AttendeeProvider>
          <ChatProvider>
            <FavoritesProvider>
              <SectionsProvider>
                <BannersProvider>
                  <HeaderProvider>
                    <AppContent />
                  </HeaderProvider>
                </BannersProvider>
              </SectionsProvider>
            </FavoritesProvider>
          </ChatProvider>
        </AttendeeProvider>
      </EventProvider>
    </Router>
  );
}

export default App;
